import { useRef, useState } from 'react';
import { ExternalLink, FilePlus2, Trash2 } from 'lucide-react';
import { DOMINIOS, TipoHabilitacao, formatarData, type DocumentoEmpresaDto, type SituacaoValidade } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { usePedirMotivo } from '../design-system/Confirmacao';
import { CampoConfig, Grupo, ouNulo, useCarga, useGravar } from './campos';

const TIPOS = Object.values(TipoHabilitacao).filter((v): v is TipoHabilitacao => typeof v === 'number');
const DESCRICOES = DOMINIOS.find((d) => d.nome === 'TipoHabilitacao')?.descricoes ?? {};
const ROTULO_TIPO = (t: TipoHabilitacao) => DESCRICOES[t] ?? 'Outros';
const SITUACOES: Record<SituacaoValidade, { texto: string; classe: string }> = {
  valido: { texto: 'Válido', classe: 'pilula--sucesso' },
  vencendo: { texto: 'Vencendo', classe: 'pilula--aguardando' },
  vencido: { texto: 'Vencido', classe: 'pilula--urgencia' },
  'sem-validade': { texto: 'Sem validade', classe: 'pilula--informacao' },
};
const data = (d: string | null) => (d ? formatarData(`${d}T12:00:00Z`) : '—');

type Novo = { nome: string; tipo: TipoHabilitacao; emitidoEm: string; validoAte: string; observacao: string };
const VAZIO: Novo = { nome: '', tipo: TipoHabilitacao.CertidaoFederal, emitidoEm: '', validoAte: '', observacao: '' };

/** Formulário do administrador: o documento, o tipo, a emissão, a validade e o arquivo. */
function NovoDocumento({ aoEnviar }: { aoEnviar: () => void }) {
  const { configuracoes } = useDependencias();
  const { gravar, gravando } = useGravar();
  const [novo, definir] = useState<Novo>(VAZIO);
  const [arquivo, definirArquivo] = useState<File | null>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const campo = (k: keyof Novo) => (v: string) => definir({ ...novo, [k]: k === 'tipo' ? Number(v) : v });
  const enviar = async () => {
    if (!arquivo) return;
    const dados = { nome: novo.nome.trim(), tipo: novo.tipo, emitidoEm: ouNulo(novo.emitidoEm), validoAte: ouNulo(novo.validoAte), observacao: ouNulo(novo.observacao) };
    if (await gravar(() => configuracoes.enviarDocumento(dados, arquivo), `${dados.nome} adicionado.`)) {
      definir(VAZIO);
      definirArquivo(null);
      aoEnviar();
    }
  };
  return (
    <Grupo
      titulo="Adicionar documento"
      acoes={
        <button type="button" className="botao botao--primario" disabled={gravando || !arquivo || novo.nome.trim().length < 2} onClick={() => void enviar()}>
          <FilePlus2 size={16} aria-hidden /> {gravando ? 'Enviando…' : 'Adicionar'}
        </button>
      }
    >
      <label className="campo config__campo config__campo--media">
        <span>Tipo</span>
        <select value={novo.tipo} onChange={(e) => campo('tipo')(e.target.value)}>
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {ROTULO_TIPO(t)}
            </option>
          ))}
        </select>
      </label>
      <CampoConfig rotulo="Nome" valor={novo.nome} definir={campo('nome')} largura="longa" />
      <CampoConfig rotulo="Emitido em" tipo="date" valor={novo.emitidoEm} definir={campo('emitidoEm')} largura="curta" />
      <CampoConfig rotulo="Válido até" tipo="date" valor={novo.validoAte} definir={campo('validoAte')} largura="curta" />
      <CampoConfig rotulo="Observação" valor={novo.observacao} definir={campo('observacao')} largura="longa" />
      <div className="campo config__campo config__campo--media">
        <span>Arquivo</span>
        <button type="button" className="botao botao--secundario" onClick={() => seletor.current?.click()}>
          {arquivo ? arquivo.name : 'Escolher arquivo'}
        </button>
        <input ref={seletor} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.md,.html,.htm,.xml,.json" onChange={(e) => definirArquivo(e.target.files?.[0] ?? null)} />
      </div>
    </Grupo>
  );
}

function Linha({ doc, podeEditar, aoExcluir }: { doc: DocumentoEmpresaDto; podeEditar: boolean; aoExcluir: () => void }) {
  const { configuracoes } = useDependencias();
  const pedirMotivo = usePedirMotivo();
  const { gravar } = useGravar();
  const situacao = SITUACOES[doc.situacao];
  const abrir = async () => {
    const janela = window.open('', '_blank');
    const { url } = await configuracoes.urlDocumento(doc.uuid);
    if (janela) janela.location.href = url;
  };
  const excluir = async () => {
    const motivo = await pedirMotivo({ titulo: 'Excluir o documento?', mensagem: `${doc.nome} sai da lista de documentos da empresa.`, rotulo: 'Excluir' });
    if (motivo && (await gravar(() => configuracoes.excluirDocumento(doc.uuid, motivo), 'Documento excluído.'))) aoExcluir();
  };
  return (
    <tr>
      <td>
        <strong>{doc.nome}</strong>
        <small>{ROTULO_TIPO(doc.tipo)}</small>
      </td>
      <td>{data(doc.emitidoEm)}</td>
      <td>{data(doc.validoAte)}</td>
      <td>
        <span className={`pilula ${situacao.classe}`}>{situacao.texto}</span>
      </td>
      <td className="config__acoes-linha">
        {doc.arquivo && (
          <button type="button" className="botao-icone" aria-label={`Abrir ${doc.arquivo.nome}`} title={doc.arquivo.nome} onClick={() => void abrir()}>
            <ExternalLink size={16} />
          </button>
        )}
        {podeEditar && (
          <button type="button" className="botao-icone" aria-label={`Excluir ${doc.nome}`} onClick={() => void excluir()}>
            <Trash2 size={16} />
          </button>
        )}
      </td>
    </tr>
  );
}

/** Documentos de habilitação (ADR-0022): todos veem a validade; só o administrador adiciona e exclui. */
export function SecaoDocumentos() {
  const { configuracoes } = useDependencias();
  const { dados, erro, recarregar } = useCarga(configuracoes.documentos);
  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  return (
    <div className="config__formulario">
      {dados.podeEditar && <NovoDocumento aoEnviar={() => void recarregar()} />}
      <p className="config__nota">Documentos vencendo nos próximos {dados.diasAviso} dias aparecem em laranja (prazo de aviso em Padrões do sistema).</p>
      {dados.documentos.length === 0 ? (
        <p className="config__vazio">Nenhum documento cadastrado.</p>
      ) : (
        <table className="config__tabela">
          <thead>
            <tr>
              <th>Documento</th>
              <th>Emissão</th>
              <th>Validade</th>
              <th>Situação</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {dados.documentos.map((d) => (
              <Linha key={d.uuid} doc={d} podeEditar={dados.podeEditar} aoExcluir={() => void recarregar()} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
